<script lang="ts">
/** One sortable column in the field menu. */
export interface GaiaSortOption {
  key: string;
  label: string;
}

export type GaiaSortDirection = 'asc' | 'desc';
</script>

<script setup lang="ts">
import { computed, h, type PropType, type VNode } from 'vue';
import { NIcon, type DropdownOption } from 'naive-ui';
import { IconArrowDown, IconArrowUp, IconCheck, IconSwapVertical } from '@/components/design/icons';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaDropdown from '../base/GaiaDropdown.vue';
import GaiaIconButton from '../base/GaiaIconButton.vue';

const props = defineProps({
  /** Columns the list can be ordered by. */
  options: {
    type: Array as PropType<GaiaSortOption[]>,
    required: true,
  },
  /** Key of the active column. */
  value: {
    type: String,
    required: true,
  },
  direction: {
    type: String as PropType<GaiaSortDirection>,
    default: 'desc',
  },
});

const emit = defineEmits<{
  'update:value': [value: string];
  'update:direction': [direction: GaiaSortDirection];
}>();

const activeLabel = computed<string>(
  () => props.options.find(option => option.key === props.value)?.label ?? ''
);

// The menu is columns only — the check marks the active one, and direction has
// its own button, so picking a column never changes the order.
const menuOptions = computed<DropdownOption[]>(() =>
  props.options.map(option => ({
    key: option.key,
    label: option.label,
    icon: option.key === props.value ? (): VNode => h(NIcon, { component: IconCheck }) : undefined,
  }))
);

const directionLabel = computed<string>(() =>
  props.direction === 'asc' ? 'Ascending' : 'Descending'
);
</script>

<template>
  <!-- List sort as two controls: the column menu, and the ascending/descending
       toggle beside it. Kept apart so either can change without the other —
       one combined menu had to overload "pick the active column" as "flip". -->
  <n-flex align="center" :size="4" :wrap="false">
    <gaia-dropdown
      :options="menuOptions"
      placement="bottom-end"
      @select="(key: string) => emit('update:value', key)"
    >
      <gaia-button size="tiny">
        <template #icon>
          <n-icon :component="IconSwapVertical" :size="12" />
        </template>
        {{ activeLabel }}
      </gaia-button>
    </gaia-dropdown>

    <gaia-icon-button
      :dimension="24"
      size="tiny"
      :title="directionLabel"
      :aria-label="`Sorted ${directionLabel.toLowerCase()} — click to reverse`"
      @click="emit('update:direction', props.direction === 'asc' ? 'desc' : 'asc')"
    >
      <n-icon :component="props.direction === 'asc' ? IconArrowUp : IconArrowDown" :size="12" />
    </gaia-icon-button>
  </n-flex>
</template>
